import React from "react";
import { Icons } from "@admin/components/icons/Icons";

interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
}

interface AdminTableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
  actionsHeader?: string;
  renderActions?: (row: T) => React.ReactNode;
  pagination?: React.ReactNode;
  maxHeight?: string; // Nueva prop para altura máxima

  // Nuevas props para funcionalidad extendida
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  onCreateClick?: (() => void) | React.ReactNode; // Puede ser función o ReactNode
  createButtonText?: string;
  title?: string;
  subtitle?: string;
}

export function AdminTable<T extends { id: string | number }>(
  props: AdminTableProps<T>
) {
  const {
    columns,
    data,
    isLoading,
    isError,
    error,
    actionsHeader = "Acciones",
    renderActions,
    pagination,
    maxHeight = "500px", // Altura por defecto

    // Nuevas props
    searchable = false,
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Buscar...",
    onCreateClick,
    createButtonText = "Agregar",
    title,
    subtitle,
  } = props;

  return (
    <div className="space-y-6">
      {/* Header con título y botón de crear */}
      {(title || onCreateClick) && (
        <div className="flex items-center justify-between">
          {(title || subtitle) && (
            <div>
              {title && (
                <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
              )}
              {subtitle && <p className="text-gray-600">{subtitle}</p>}
            </div>
          )}
          <div className="flex items-center space-x-3">
            {onCreateClick &&
              (typeof onCreateClick === "function" ? (
                <button
                  onClick={onCreateClick}
                  className="flex items-center px-4 py-2 bg-[#282828] text-white rounded-lg hover:bg-[#3a3a3a] transition-colors"
                >
                  <Icons.Plus size={20} className="mr-2" />
                  {createButtonText}
                </button>
              ) : (
                onCreateClick
              ))}
          </div>
        </div>
      )}

      {/* Barra de búsqueda */}
      {searchable && onSearchChange && (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icons.Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight }}>
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={col.header + idx}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 ${
                      col.className || ""
                    }`}
                  >
                    {col.header}
                  </th>
                ))}
                {renderActions && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {actionsHeader}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length + (renderActions ? 1 : 0)}
                    className="text-center py-8 text-gray-400"
                  >
                    Cargando datos...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={columns.length + (renderActions ? 1 : 0)}
                    className="text-center py-8 text-red-500"
                  >
                    Error al cargar datos: {error}
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (renderActions ? 1 : 0)}
                    className="text-center py-8 text-gray-400"
                  >
                    No se encontraron resultados.
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {columns.map((col, idx) => (
                      <td
                        key={col.header + idx}
                        className="px-6 py-4 whitespace-nowrap"
                      >
                        <div className="text-sm text-gray-900">
                          {typeof col.accessor === "function"
                            ? col.accessor(row)
                            : (row[col.accessor] as React.ReactNode)}
                        </div>
                      </td>
                    ))}
                    {renderActions && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(row)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {pagination && (
          <div className="border-t border-gray-200">{pagination}</div>
        )}
      </div>
    </div>
  );
}

